import { effectiveEnergy, type Card } from '@hmawiw/shared';
import { useEffect, useState } from 'react';
import { useNow } from '../../hooks/useMedia';
import { useT } from '../../i18n';
import { boardCards, useCards, useBoardCards } from '../../store/cards';
import { useEvents } from '../../store/events';
import { useSettings } from '../../store/settings';
import { useUi } from '../../store/ui';
import controls from '../controls.module.css';
import { Cross } from '../icons';
import styles from './Levers.module.css';

const KEY = 'hmawiw.moment';

function readAnswered(): number {
  try {
    return Number(localStorage.getItem(KEY) ?? 0);
  } catch {
    return 0;
  }
}

/** The card to offer when energy is high: the top Dreading Must, else the top Dreading card. */
export function momentCard(cards: Record<string, Card>, now: number): Card | null {
  const dreading = boardCards(cards, now).filter((c) => c.motivation === 0);
  if (dreading.length === 0) return null;
  return dreading.sort((a, b) => a.band - b.band || a.rank - b.rank)[0] ?? null;
}

/** "Pick the moment": high energy is the time for a dreaded card, so one is offered, once per setting. */
export function MomentBar() {
  const t = useT();
  const now = useNow();
  const energyNow = useSettings((s) => s.energyNow);
  const cards = useBoardCards();
  const openSheet = useUi((s) => s.openSheet);
  const log = useEvents((s) => s.log);
  const [answered, setAnswered] = useState(readAnswered);
  const setAt = energyNow?.setAt ?? 0;
  const high = effectiveEnergy(energyNow, now) === 3 && setAt > answered;
  const card = high ? momentCard(cards, now) : null;
  const answer = (how: 'open' | 'later') => {
    try {
      localStorage.setItem(KEY, String(setAt));
    } catch {
      // no storage
    }
    setAnswered(setAt);
    if (card) void log('offer', card.id, { kind: 'moment', answer: how });
    if (how === 'open' && card) openSheet(card.id);
  };
  useEffect(() => {
    if (card) void log('offer', card.id, { kind: 'moment', answer: 'shown' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card?.id, setAt]);
  if (!card) return null;
  return (
    <div className={styles.bar} role="status" data-moment="">
      <span className={styles.text}>
        <span className={styles.lead}>{t.t('moment.lead')}</span>
        <span className={styles.title}>{card.title}</span>
      </span>
      <button type="button" className={`${controls.btn} ${controls.primary} ${styles.action}`} onClick={() => answer('open')}>
        {t.t('moment.open')}
      </button>
      <button type="button" className={controls.iconbtn} onClick={() => answer('later')} aria-label={t.t('moment.later')} title={t.t('moment.later')}>
        <Cross size={18} />
      </button>
    </div>
  );
}
